import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  IntakeFormData,
  isRecurringPackage,
  isSensitivePackage,
} from "@/domain/intake";
import type { PackageOfferingDto } from "@shared/types/PackageOfferingDto";

interface ReviewStepProps {
  formData: IntakeFormData;
  selectedPackage: PackageOfferingDto | null;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting: boolean;
}

export function ReviewStep({
  formData,
  selectedPackage,
  onSubmit,
  onBack,
  isSubmitting,
}: ReviewStepProps) {
  const { projectBasics, confidentiality } = formData;
  const isRecurring = isRecurringPackage(projectBasics.selectedPackageId);
  const ndaRequired =
    confidentiality.ndaRequested ||
    (isSensitivePackage(projectBasics.selectedPackageId) &&
      confidentiality.willShareSensitiveData);

  return (
    <Card className="glass-card border-gradient">
      <CardContent className="p-6 md:p-8">
        <h2 className="font-display text-2xl font-bold mb-2">
          Review & Submit
        </h2>
        <p className="text-muted-foreground mb-6">
          Review your information before submitting. After submission, we'll
          verify your email before scheduling.
        </p>

        <div className="space-y-6">
          <div className="rounded-lg border border-border/50 divide-y divide-border/50">
            <div className="p-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                Package
              </p>
              {selectedPackage ? (
                <div className="flex items-center gap-3">
                  <p className="font-semibold">{selectedPackage.name}</p>
                  <Badge variant="secondary" className="text-xs">
                    {isRecurring
                      ? "Recurring"
                      : selectedPackage.timelineEstimate}
                  </Badge>
                </div>
              ) : (
                <p className="text-muted-foreground">No package selected</p>
              )}
            </div>

            <div className="p-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                Contact
              </p>
              <div className="grid md:grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground">Name</p>
                  <p className="font-medium">{projectBasics.name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Company</p>
                  <p className="font-medium">{projectBasics.companyName}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <p className="font-medium">{projectBasics.email}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Time Zone</p>
                  <p className="font-medium">{projectBasics.timezone}</p>
                </div>
              </div>
            </div>

            <div className="p-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                Project
              </p>
              <div className="grid md:grid-cols-2 gap-3 text-sm">
                {isRecurring ? (
                  <>
                    <div className="md:col-span-2">
                      <p className="text-muted-foreground">Current Situation</p>
                      <p className="font-medium">
                        {projectBasics.currentSituation}
                      </p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-muted-foreground">Improvement Goals</p>
                      <p className="font-medium">
                        {projectBasics.improvementGoals}
                      </p>
                    </div>
                    {projectBasics.tierIntent && (
                      <div>
                        <p className="text-muted-foreground">Preferred Tier</p>
                        <p className="font-medium capitalize">
                          {projectBasics.tierIntent}
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="md:col-span-2">
                    <p className="text-muted-foreground">Summary</p>
                    <p className="font-medium">
                      {projectBasics.projectSummary}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-muted-foreground">Company Size</p>
                  <p className="font-medium">
                    {projectBasics.companySize} employees
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                Confidentiality
              </p>
              <div className="flex items-center gap-2">
                {ndaRequired ? (
                  <>
                    <ShieldCheck className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">
                      NDA required — will be signed after verification
                    </span>
                  </>
                ) : (
                  <span className="text-sm text-muted-foreground">
                    No NDA requested
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
            <p className="text-sm font-medium mb-2">What happens next</p>
            <ol className="text-sm text-muted-foreground space-y-1.5 list-decimal list-inside">
              <li>We'll send a verification code to {projectBasics.email}</li>
              {ndaRequired && (
                <li>
                  You'll review and sign a brief NDA (less than 2 minutes)
                </li>
              )}
              <li>You'll schedule your discovery call via Calendly</li>
            </ol>
          </div>

          <div className="pt-4 flex justify-between">
            <Button variant="outline" onClick={onBack}>
              Back
            </Button>
            <Button onClick={onSubmit} disabled={isSubmitting} size="lg">
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit & Verify"
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
